import React, { FC, useEffect, useState } from 'react';
import { components } from 'react-select';
import {
  Avatar,
  Button,
  Field,
  Icon,
  IconButton,
  mergeTailwindClasses,
  Select,
  TextArea,
  Tooltip,
} from '@kontentino/ui';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SocialMediaTypeSelect from 'app/components/modals/addNewProfile/addNewProfileManually/SocialMediaTypeSelect';
import clsx from 'clsx';
import { useBrandHubNewCreativeForm } from 'app/modules/brandHub/components/brandHubCreatives/brandHubNewCreativeForm/BrandHubNewCreativeFormProvider';
import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import { BrandHubApi } from 'app/modules/brandHub/api/brandHub';
import useBoolean from 'utils/hooks/useBoolean';
import BrandHubManageProfileModalForm from 'app/modules/brandHub/components/modals/brandHubManageProfileModal/brandHubManageProfileModalForm';
import Modal from 'components/shared/Modal';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';
import { BrandProfile } from 'app/modules/brandHub/types';
import { GenerationsApi } from 'app/modules/posts/api/GenerationsApi';
import { useLastSelectOptions } from 'app/hooks/useLastSelectOptions';
import WebCrawlLinkField from 'app/modules/brandHub/components/WebCrawlLinkField';

type Props = {
  className?: string;
  initialExpanded?: boolean;
  isLoadingGoal?: boolean;
};

const BrandHubNewCreativeForm: FC<Props> = ({
  className,
  initialExpanded,
  isLoadingGoal,
}) => {
  const [isExpanded, setIsExpanded] = useBoolean(initialExpanded);
  const [manageProfileOpen, setIsManageProfileOpen] = useBoolean(false);
  const [newBrandName, setNewBrandName] = useState<string | undefined>();
  const [profileToEdit, setProfileToEdit] = useState<
    BrandProfile | undefined
  >();
  const { form, brandHubProfiles, initialValues } =
    useBrandHubNewCreativeForm();
  const creativesOptions = useQuery(
    queryKey.brandHubCreativesOptions(),
    BrandHubApi.creativesOptions,
  );
  const { t } = useTranslation();
  const translateTextOptions = useQuery(
    queryKey.transformTextOptions(),
    GenerationsApi.getTransformTextOptions,
  );
  const lastSelectOptions = useLastSelectOptions();

  const isLoading =
    isLoadingGoal ||
    brandHubProfiles.isLoading ||
    creativesOptions.isLoading ||
    translateTextOptions.isLoading;

  function onClose() {
    setIsManageProfileOpen.off();
    setNewBrandName(undefined);
    setProfileToEdit(undefined);
  }

  useEffect(() => {
    const values = form.getValues();

    function preselectBranProfile() {
      if (values.brandProfileId || initialValues?.brandProfileId) return;

      if (!values.brandProfileId && lastSelectOptions.brandProfileId[0]) {
        const preselectBranProfile = brandHubProfiles.data?.find(
          (profile) => profile.id === lastSelectOptions.brandProfileId[0].value,
        );

        if (preselectBranProfile) {
          form.setValue('brandProfileId', preselectBranProfile.id);
        }
      }
    }

    async function preselectGoal() {
      try {
        // Only set goal if we have initial goal value and data is done loading
        if (!isLoadingGoal && initialValues?.goal && !values.goal) {
          form.setValue('goal', initialValues.goal);
        }
      } catch (error: unknown) {
        if (isLoadingGoal) {
          form.setValue('goal', '');
        }
      }
    }

    preselectBranProfile();
    preselectGoal();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    brandHubProfiles.data,
    lastSelectOptions.brandProfileId,
    creativesOptions.data,
    isLoadingGoal,
    initialValues?.goal,
  ]);

  return (
    <div
      className={mergeTailwindClasses(
        clsx('tw-relative tw-space-y-4 tw-overflow-auto', className),
      )}
    >
      {isLoading ? (
        <div className="tw-space-y-4 tw-p-4">
          <Skeleton height={20} width="30%" />
          <Skeleton height={80} />
          <Skeleton height={20} width="40%" />
          <Skeleton height={40} />
          <div className="tw-flex tw-gap-4">
            <div className="tw-w-3/5">
              <Skeleton height={20} width="30%" />
              <Skeleton height={40} className="tw-mt-2" />
            </div>
            <div className="tw-w-2/5">
              <Skeleton height={20} width="40%" />
              <Skeleton height={40} className="tw-mt-2" />
            </div>
          </div>
        </div>
      ) : (
        <>
          {initialValues?.brandProfileId && (
            <Field.Group>
              <Field.Label required>
                {t('brandHub.targetSocialMedia')}
              </Field.Label>
              <Controller
                name="pageType"
                control={form.control}
                render={({ field: { value, onChange } }) => (
                  <div
                    data-name="brand-hub_create-creative_page-type_select-wrapper"
                    data-cy="brand-hub_create-creative_page-types_elect-wrapper"
                  >
                    <SocialMediaTypeSelect
                      data-name="brand-hub_create-creative_page-type"
                      data-cy="brand-hub_create-creative_page-type"
                      value={value ? { value } : undefined}
                      onSelect={(option) => onChange(option)}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (styles: any) => ({
                          ...styles,
                          zIndex: 1010,
                        }),
                      }}
                    />
                  </div>
                )}
              />
              {!!form.formState.errors.pageType?.message && (
                <Field.Error>
                  {form.formState.errors.pageType?.message}
                </Field.Error>
              )}
            </Field.Group>
          )}
          {!initialValues?.brandProfileId && (
            <>
              <Field.Group>
                <Field.Label required>{t('brandVoice')}</Field.Label>
                <Controller
                  name="brandProfileId"
                  control={form.control}
                  render={({ field: { value, onChange } }) => {
                    const selectedProfile = brandHubProfiles.data?.find(
                      (profile) => profile.id === value,
                    );
                    const selectValue = selectedProfile
                      ? {
                          value: selectedProfile.id,
                          label: selectedProfile.name,
                        }
                      : null;

                    const groupedOptions = lastSelectOptions.getGroupedOptions(
                      'brandProfileId',
                      brandHubProfiles?.data?.map((profile) => ({
                        value: `${profile.id}`,
                        label: profile.name,
                      })) || [],
                    );

                    return (
                      <div
                        data-name="brand-hub_create-creative_brand-profile_select-wrapper"
                        data-cy="brand-hub_create-creative_brand-profile_select-wrapper"
                      >
                        <Select
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (styles) => ({
                              ...styles,
                              zIndex: 1010,
                            }),
                          }}
                          value={selectValue}
                          isClearable
                          placeholder={t('selectBrandVoice')}
                          formatOptionLabel={(option) => (
                            <div className="tw-flex tw-items-center">
                              <Avatar
                                size={26}
                                name={option.label}
                                src={
                                  brandHubProfiles?.data?.find(
                                    (profile) => profile.id === option.value,
                                  )?.logoSrc
                                }
                              />
                              <span className="tw-ml-2">{option.label}</span>
                            </div>
                          )}
                          components={{
                            Option: ({ children, ...props }) => (
                              <components.Option {...props}>
                                <div className="tw-flex tw-items-center">
                                  <Avatar
                                    size={26}
                                    name={props.data.label}
                                    src={
                                      brandHubProfiles?.data?.find(
                                        (profile) =>
                                          profile.id === props.data.value,
                                      )?.logoSrc
                                    }
                                    className="tw-mr-2"
                                  />
                                  <span className="tw-truncate tw-font-regular">
                                    {props.data.label}
                                  </span>
                                  <Tooltip content={t('brandHub.edit')}>
                                    <IconButton
                                      variant="ghost"
                                      size="small"
                                      className="tw-ml-auto tw-hidden tw-text-grayscale-100 data-[state=focused]:tw-block"
                                      data-state={
                                        props.isFocused
                                          ? 'focused'
                                          : 'unfocused'
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const profileToEdit =
                                          brandHubProfiles.data?.find(
                                            (profile) =>
                                              profile.id === props.data.value,
                                          );

                                        if (profileToEdit) {
                                          setIsManageProfileOpen.on();
                                          setProfileToEdit(profileToEdit);
                                        }
                                      }}
                                    >
                                      <Icon icon={faPen} />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              </components.Option>
                            ),
                          }}
                          onChange={(option) => {
                            onChange(option?.value);
                            if (option) {
                              lastSelectOptions.addOption(
                                'brandProfileId',
                                option,
                              );
                            }
                          }}
                          options={groupedOptions}
                          isCreatable
                          onCreateOption={(value) => {
                            setNewBrandName(value);
                            setIsManageProfileOpen.on();
                          }}
                        />
                        <Button
                          onClick={() => setIsManageProfileOpen.on()}
                          data-name="brand-create_creative_hub_new-brand-profile_create-profile"
                          variant="tertiary"
                          size="small"
                          className="tw-mt-2"
                        >
                          {t('brandHub.createBrandProfile')}
                        </Button>
                      </div>
                    );
                  }}
                />
                {!!form.formState.errors.brandProfileId?.message && (
                  <Field.Error>
                    {form.formState.errors.brandProfileId?.message}
                  </Field.Error>
                )}
              </Field.Group>
            </>
          )}
          <Field.Group>
            <Field.Label required>{t('goalOfYourPost')}</Field.Label>
            <TextArea
              rows={3}
              resize
              {...form.register('goal')}
              data-name="brand-hub_create-creative_goal"
              data-cy="brand-hub_create-creative_goal"
              placeholder={
                isLoading ? t('loading') : t('goalOfYourPostPlaceholder')
              }
              disabled={isLoading}
            />
            {!!form.formState.errors.goal?.message && (
              <Field.Error>{form.formState.errors.goal?.message}</Field.Error>
            )}
          </Field.Group>
          <Field.Group>
            <Field.Label required>{t('brandHubReferenceLink')}</Field.Label>
            <Field.Caption>
              {t('creativeFormReferenceLinkDescription')}
            </Field.Caption>
            <Controller
              control={form.control}
              name="webCrawl"
              render={({ field }) => (
                <WebCrawlLinkField
                  value={field.value}
                  onChange={field.onChange}
                  dataNamePrefix="brand-hub_create-creative"
                  autoFetch
                />
              )}
            />
            {!!form.formState.errors.webCrawl?.message && (
              <Field.Error>
                <>{form.formState.errors.webCrawl?.message}</>
              </Field.Error>
            )}
          </Field.Group>
          <div className="tw-flex tw-gap-4">
            <Field.Group className="tw-w-3/5">
              <Field.Label required>{t('toneOfVoice')}</Field.Label>
              <Controller
                name="toneOfVoice"
                control={form.control}
                render={({ field: { value, onChange } }) => {
                  const selectValue =
                    creativesOptions?.data?.toneOfVoice?.find(
                      (option) => option.value === value,
                    ) || null;

                  return (
                    <div
                      data-name="brand-hub_create-creative_tone-of-voice_select-wrapper"
                      data-cy="brand-hub_create-creative_tone-of-voice_select-wrapper"
                    >
                      <Select
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (styles) => ({
                            ...styles,
                            zIndex: 1010,
                          }),
                        }}
                        value={selectValue}
                        isClearable
                        onChange={(option) => {
                          onChange(option?.value);
                        }}
                        options={creativesOptions.data?.toneOfVoice || []}
                      />
                    </div>
                  );
                }}
              />
              {!!form.formState.errors.toneOfVoice?.message && (
                <Field.Error>
                  {form.formState.errors.toneOfVoice?.message}
                </Field.Error>
              )}
            </Field.Group>
            <Field.Group className="tw-w-2/5">
              <Field.Label required>{t('lengthOfCreatives')}</Field.Label>
              <Controller
                name="creativeLength"
                control={form.control}
                render={({ field: { value, onChange } }) => {
                  const selectValue =
                    creativesOptions?.data?.creativeLength?.find(
                      (option) => option.value === value,
                    ) || null;

                  return (
                    <div
                      data-name="brand-hub_create-creative_length_select-wrapper"
                      data-cy="brand-hub_create-creative_length_select-wrapper"
                    >
                      <Select
                        data-name="brand-hub_create-creative_length"
                        data-cy="brand-hub_create-creative_length"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (styles) => ({
                            ...styles,
                            zIndex: 1010,
                          }),
                        }}
                        value={selectValue}
                        formatOptionLabel={({ value }) =>
                          t(`creativesLength.${value || 'notSpecified'}`)
                        }
                        onChange={(option) => onChange(option?.value)}
                        options={creativesOptions?.data?.creativeLength || []}
                      />
                    </div>
                  );
                }}
              />
              {!!form.formState.errors.creativeLength?.message && (
                <Field.Error>
                  {form.formState.errors.creativeLength?.message}
                </Field.Error>
              )}
            </Field.Group>
          </div>
          {!isExpanded && (
            <Button
              variant="ghost"
              size="small"
              data-name="brand-hub_create-creative-expand-button"
              data-cy="brand-hub_create-creative-expand-button"
              onClick={() => setIsExpanded.on()}
              className="tw-px-0 tw-py-0 tw-text-primary-100 hover:tw-bg-transparent hover:tw-underline"
            >
              {t('moreOptions')}
            </Button>
          )}
          {isExpanded && (
            <>
              <Field.Group>
                <Field.Label required>{t('outputLanguage')}</Field.Label>
                <Controller
                  name="language"
                  control={form.control}
                  render={({ field: { onChange, value, ref } }) => {
                    const languages =
                      translateTextOptions.data?.options.language ?? [];

                    const selectedValue =
                      languages.find((language) => language.value === value) ??
                      null;

                    const groupedOptions = lastSelectOptions.getGroupedOptions(
                      'language',
                      languages,
                    );

                    return (
                      <div
                        ref={ref}
                        tabIndex={0}
                        data-name="translate-text_language_select-wrapper"
                        data-cy="translate-text_language_select-wrapper"
                      >
                        <Select
                          options={groupedOptions}
                          isClearable
                          placeholder={t('brandHub.usingBrandVoiceLanguage')}
                          onChange={(option) => {
                            if (option?.value) {
                              lastSelectOptions.addOption('language', option);
                            }
                            onChange(option?.value ?? null);
                          }}
                          value={selectedValue}
                          menuPortalTarget={document.body}
                          menuPlacement="auto"
                          styles={{
                            menuPortal: (styles) => ({
                              ...styles,
                              zIndex: 1010,
                            }),
                          }}
                        />
                      </div>
                    );
                  }}
                />
                {form.formState.errors.language?.message && (
                  <Field.Error>
                    {form.formState.errors.language?.message}
                  </Field.Error>
                )}
              </Field.Group>
              <Field.Group>
                <Field.Label required>{t('countOfCreatives')}</Field.Label>
                <Controller
                  name="creativeCount"
                  control={form.control}
                  render={({ field: { value, onChange } }) => (
                    <div
                      data-name="brand-hub_create-creative_count_select-wrapper"
                      data-cy="brand-hub_create-creative_count_select-wrapper"
                    >
                      <Select
                        data-name="brand-hub_create-creative_count"
                        data-cy="brand-hub_create-creative_count"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (styles) => ({
                            ...styles,
                            zIndex: 1010,
                          }),
                        }}
                        value={{
                          value,
                          label: t('countOfCreatives', { count: value }),
                        }}
                        onChange={(option) => onChange(option?.value)}
                        options={creativesOptions.data?.counts || []}
                      />
                    </div>
                  )}
                />
                {!!form.formState.errors.creativeCount?.message && (
                  <Field.Error>
                    {form.formState.errors.creativeCount?.message}
                  </Field.Error>
                )}
              </Field.Group>
            </>
          )}
          {isExpanded && (
            <Button
              variant="ghost"
              size="small"
              data-name="brand-hub_create-creative-collapse-button"
              data-cy="brand-hub_create-creative-collapse-button"
              onClick={() => setIsExpanded.off()}
              className="tw-px-0 tw-py-0 tw-text-primary-100 hover:tw-bg-transparent hover:tw-underline"
            >
              {t('lessOptions')}
            </Button>
          )}
        </>
      )}

      <Modal
        open={manageProfileOpen}
        onClose={setIsManageProfileOpen.off}
        dataName={{
          closeButton: 'brand-hub_manage-brand-profile_close',
          wrapper: 'brand-hub_manage-brand-profile_modal',
        }}
        dataCy={{
          closeButton: 'brand-hub_manage-brand-profile_close',
          wrapper: 'brand-hub_manage-brand-profile_modal',
        }}
        classNames={{
          root: '!tw-z-[1002]',
        }}
      >
        <BrandHubManageProfileModalForm
          isProfileEdit={!!profileToEdit}
          onClose={onClose}
          profileToEdit={profileToEdit}
          initialValues={{
            name: newBrandName,
          }}
          eventHandlers={{
            onProfileSave: (profile) => {
              form.setValue('brandProfileId', profile.id);
            },
            onRemainingProfilesChange: (profiles) => {
              const profileId = form.getValues().brandProfileId;

              if (
                profileId &&
                !profiles.find(
                  (remainingProfiles) => remainingProfiles.id === profileId,
                )
              ) {
                form.setValue('brandProfileId', profiles[0]?.id || undefined);
              }
            },
          }}
        />
      </Modal>
    </div>
  );
};

export default BrandHubNewCreativeForm;
